import { Layout } from "../../components/Layout";
import {
  CHECKOUT_STATUS,
  type CheckoutStepOptions,
  type ProblemItemType,
} from "../../../../types/checkout";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, BUTTON_COLORS, BUTTON_TYPE } from "../../components/Button";
import { useCart } from "../../providers/cart";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ArtList } from "../../components/ArtList";
import { Loader } from "../../components/Loader";
import { usePermissions } from "../../providers/authProvider";
import { useCheckoutData, CheckoutState } from "../../hooks/useCheckoutData";
import { log } from "../../utils/logger";
import {
  CART_URL,
  CHECKOUT_SUCCESS_URL,
  CHECKOUT_URL,
  USER_AUTH_URL,
} from "../../config";
import {
  CheckoutSteps,
  SubmitCheckoutStepData,
} from "../../components/checkout/CheckoutSteps";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Errors } from "../../../../shared-utils/error";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
);
// Scoped logger for checkout page
const logger = log.scope("CheckoutPage");

const steps = ["email", "location", "payment"] as CheckoutStepOptions[];

// Determine initial step based on checkout state
const getInitialStep = (state: CheckoutState): number => {
  if (state.status === "error" && state.error) {
    if (!state.errorInfo) {
      //TODO, Return finished state if finished, or generic error state if problem items or unknown error.
      return 0;
    }

    if (
      state.errorInfo?.problemLocation == "checkoutItems" ||
      state.errorInfo.problemLocation === "contactEmail"
    )
      return 0;
    if (state.errorInfo?.problemLocation == "deliveryLocation") return 1;
    if (state.errorInfo?.problemLocation == "payment") return 1;
  }
  if (state.status === "ready") {
    if (!state.data.checkout.contactEmail) return 0; // Email step
    if (!state.data.checkout.deliveryLocation) return 1; // Location step
    return 2; // Payment step
  }
  return 0;
};
/**
 * Checkout
 * # Path params
 *   - checkoutId?: string
 *
 * If the user is not logged in, ask them to
 *
 * If a checkout id DOES NOT HAVE A checkout, a new one will be initialized with the cartSlug, userId, email, and items
 * TODO: If a user has a pending checkout, block, auto resolce, or offer to resolve
 *
 * If the checkout id has an EXISTING PENDING checkout, continue the checkout from where they left off
 * // DOUBLE CHECK IDS
 * // DO NOT OVERWRITE EXISTING DATA
 *
 * If the checkout id has an EXISTING FINISHED checkout
 *  - and the items match, continue to the checkout success page
 *  - and the items don't match, change the cart slug and continue with that one
 *
 * @returns JSX.Element
 */

export default function Checkout() {
  const navigate = useNavigate();
  const params = useParams();
  const userPermissions = usePermissions();
  const { removeItem, addItem, cart, resetCart } = useCart();
  const checkoutId = params.checkoutId;

  const checkoutIdParamSetUp = useRef(!!params.checkoutId);
  useEffect(() => {
    if (!checkoutIdParamSetUp.current) {
      checkoutIdParamSetUp.current = true;
      navigate(CHECKOUT_URL(cart.slug), { replace: true });
      return;
    }
  }, [params.checkoutId, checkoutIdParamSetUp, navigate]);

  const [checkoutState, patchCheckout, getCheckout] = useCheckoutData();

  logger.debug("Checkout render", {
    checkoutId,
    userId: userPermissions?.data?.user?.id,
    checkoutStatus: checkoutState.status,
  });

  // SUCCESS PAYMENT REDIRECT STATES
  const [searchParams] = useSearchParams();
  const payment_intent_client_secret = searchParams.get(
    "payment_intent_client_secret",
  );
  const redirect_status = searchParams.get("redirect_status");
  // SUCCESS PAYMENT REDIRECT STATES

  // SUCCESS PAYMENT REDIRECT LOGIC
  const handleSuccessCalled = useRef(false);

  const handleSuccess = useCallback(() => {
    if (checkoutId) {
      if (cart.slug === checkoutId) {
        resetCart?.();
      }
      navigate(CHECKOUT_SUCCESS_URL(checkoutId), { replace: true });
    }
  }, [checkoutId, navigate, resetCart]);

  useEffect(() => {
    if (
      !handleSuccessCalled.current &&
      payment_intent_client_secret &&
      redirect_status === "succeeded"
    ) {
      logger.info("Handling payment redirect success", {
        checkoutState,
        redirectStatus: redirect_status,
      });
      handleSuccessCalled.current = true;
      handleSuccess();
    }
  }, [payment_intent_client_secret, redirect_status, handleSuccess]);
  const init = useRef(true);
  useEffect(() => {
    if (init.current === false) {
      // Already initialized
      return;
    }

    if (userPermissions?.loading || !userPermissions) {
      // NOT READY
      return;
    }

    if (!userPermissions.data?.loggedIn || !userPermissions.data.user?.email) {
      // NOT logged in, ask them to
      init.current = false;
      navigate(USER_AUTH_URL("login", { flow: "checkout" }));
      return;
    }

    const user = userPermissions?.data?.user;
    console.log("call", user, checkoutId, cart.slug);

    if (user && checkoutId) {
      // Ready to fetch
      init.current = false;
      getCheckout({ id: checkoutId }).then((response) => {
        console.log("getCheckout response", response);

        // Doesn't exist, let's create it
        if (response.success === false && response.code === Errors.NOT_FOUND) {
          if (!cart.items?.length) {
            navigate(CART_URL());
          }

          // TODO: If a user has a pending checkout, block, auto resolce, or offer to resolve
          patchCheckout({
            id: checkoutId,
            userId: user.id,
            items: cart.items,
            contactEmail: user.email,
          });
        } else if (
          response.success === true &&
          response.data.checkout.status === CHECKOUT_STATUS.FINISHED &&
          !handleSuccessCalled.current
        ) {
          //TODO if the cart slug matches the checkoutId,
          // but items don't match - ???
          handleSuccess();
        }
      });
      return;
    }
  }, [
    userPermissions?.loading,
    userPermissions?.data?.user,
    checkoutId,
    cart.items,
    navigate,
    handleSuccess,
  ]);

  const [step, setStep] = useState<number>(getInitialStep(checkoutState));

  useEffect(() => {
    if (checkoutState.status !== "loading") {
      setStep(getInitialStep(checkoutState));
    }
  }, [checkoutState.status]);

  // Render different views based on checkout state
  const renderContent = () => {
    // Loading state
    if (
      checkoutState.status === "loading" ||
      checkoutState.status === "idle" ||
      !checkoutId
    ) {
      return (
        <div className="p-5">
          <Loader>Loading checkout...</Loader>
        </div>
      );
    }

    const handleSubmit = async (submission: SubmitCheckoutStepData) => {
      logger.info("Updating checkout", {
        checkoutId,
        type: submission.type,
        data: { ...submission.data },
      });

      if (submission.type === "email") {
        patchCheckout({
          id: checkoutId,
          contactEmail: submission.data.email,
        });
        // router.reload();
      } else if (submission.type === "location") {
        patchCheckout({
          id: checkoutId,
          deliveryLocation: {
            ...submission.data,
            type: "location",
          },
        });
      } else if (submission.type === "payment") {
        // Payment handled by Stripe
        logger.info("Payment step submitted", { checkoutId });
      } else {
        throw Error("Type not recognized: '" + (submission as any).type + "'");
      }
    };
    // Error state (validation/cart errors)
    if (checkoutState.status === "error") {
      const { error, errorInfo } = checkoutState;
      // Item validation error with problem items
      if (
        errorInfo?.problemLocation === "checkoutItems" &&
        errorInfo.problemItems
      ) {
        return (
          <div className="p-5">
            <div>
              <h1>{error}</h1>
            </div>
            <div>
              <ArtList
                items={errorInfo.problemItems.map(
                  (probItem: ProblemItemType) => ({
                    ...probItem.item,
                    __meta: { error: probItem.error },
                  }),
                )}
                noArt={<>NO ITEMS IN CART</>}
                getArtConfig={(item, meta: any) => {
                  return {
                    text: meta?.error.message
                      ? meta.error.message
                      : "an unknown issue",
                    actionBelow: {
                      actionComponent: meta?.error.solutions ? (
                        <>
                          {meta.error.solutions.map(
                            (solution: "addToCart" | "removeFromCart") => (
                              <div key={solution} className="-mx-1">
                                {solution === "addToCart" ? (
                                  <Button
                                    btnType="xs"
                                    btnColor="primary"
                                    onClick={() => addItem(item)}
                                  >
                                    Add to cart
                                  </Button>
                                ) : solution === "removeFromCart" ? (
                                  cart.items.find(
                                    (cartItem) => cartItem.slug === item.slug,
                                  ) ? (
                                    <Button
                                      btnType="xs"
                                      btnColor="primary"
                                      onClick={() => removeItem(item)}
                                    >
                                      Remove from cart
                                    </Button>
                                  ) : null
                                ) : null}
                              </div>
                            ),
                          )}
                        </>
                      ) : (
                        <div className="-mx-1">
                          {cart.items.find(
                            (cartItem) => cartItem.slug === item.slug,
                          ) ? (
                            <Button
                              btnType="xs"
                              btnColor="primary"
                              onClick={() => removeItem(item)}
                            >
                              Remove from cart
                            </Button>
                          ) : null}
                        </div>
                      ),
                    },
                    tileClassName:
                      "rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg",
                    href: "",
                  };
                }}
              />
            </div>
            <div className="flex justify-center">
              <Link
                to={CART_URL()}
                className={BUTTON_TYPE.large + BUTTON_COLORS.primary}
              >
                Back to Cart
              </Link>
            </div>
          </div>
        );
      }

      // Location error (tax calculation issue)
      if (
        errorInfo?.problemLocation === "deliveryLocation" &&
        errorInfo.locationError
      ) {
        return (
          <div className="flex flex-col 2xl:flex-row items-start p-4">
            <div className="flex-1 max-w-full">
              {Elements ? (
                <Elements
                  options={{
                    clientSecret: errorInfo.checkout?.payment?.clientSecret,
                    appearance: {
                      theme: "stripe" as const,
                    },
                  }}
                  stripe={stripePromise}
                >
                  <CheckoutSteps
                    locationError={{ message: errorInfo.locationError }}
                    onSubmit={handleSubmit}
                    setStepIdx={setStep}
                    stepIdx={step}
                    steps={steps}
                    contactEmail={
                      errorInfo?.checkout?.contactEmail ||
                      userPermissions?.data?.user?.email
                    }
                    deliveryLocation={
                      errorInfo?.checkout?.deliveryLocation ?? undefined
                    }
                    payment={errorInfo?.checkout?.payment}
                  />
                </Elements>
              ) : null}
            </div>
            <div className="flex-1 sticky top-0">
              <ArtList
                items={cart.items.map((item) => ({
                  ...item,
                  images: item.images?.slice(0, 1),
                }))}
                noArt={<>NO ITEMS IN CART</>}
                getArtConfig={() => ({
                  tileClassName:
                    "rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg",
                  href: "",
                  height: 200,
                  hoverHeight: 200,
                })}
              />
            </div>
          </div>
        );
      }

      // Generic error
      return (
        <div className="p-5">
          <div>
            <h1>{error}</h1>
          </div>
          <div className="flex justify-center">
            <Link
              to={CART_URL()}
              className={BUTTON_TYPE.large + BUTTON_COLORS.primary}
            >
              Try again
            </Link>
          </div>

          <div>
            <h2>More info:</h2>
            <p>{JSON.stringify(errorInfo as any, null, 2)}</p>
          </div>
        </div>
      );
    }

    // Redirect success state (payment completed)
    if (checkoutState.status === "redirect-success") {
      const { orderSlug, contactEmail, deliveryLocation, status, payment } =
        checkoutState.data.checkout;
      return (
        <div className="flex-1 max-w-full">
          {!payment_intent_client_secret ? (
            "Something went terribly wrong, no `payment_intent_client_secret`"
          ) : orderSlug ? (
            <div>
              <div className=" p-auto flex flex-col mx-2">
                {Elements ? (
                  <Elements
                    options={{
                      clientSecret: payment?.clientSecret,
                      appearance: {
                        theme: "stripe" as const,
                      },
                    }}
                    stripe={stripePromise}
                  >
                    <CheckoutSteps
                      // cartSlug={cartSlug}
                      orderSlug={orderSlug}
                      onSubmit={() => {}}
                      stepIdx={2}
                      showAll
                      steps={["email", "location", "status"]}
                      contactEmail={contactEmail}
                      deliveryLocation={deliveryLocation}
                      status={{ status }}
                    />
                  </Elements>
                ) : null}
                ;
              </div>
              <div className="p-10">
                <Link
                  className={BUTTON_TYPE.large + BUTTON_COLORS.primary}
                  id="retry-button"
                  to="/"
                >
                  Shop for more!
                </Link>
              </div>
            </div>
          ) : (
            <div>
              Something went wrong with the order. Please reach out to{" "}
              <a href="mailto:sales@doridium.com">sales@doridium.com</a> to see
              if your order went through
            </div>
          )}
        </div>
      );
    }

    // Ready state (normal checkout flow)
    if (checkoutState.status === "ready") {
      const { payment, deliveryLocation, contactEmail } =
        checkoutState.data.checkout;
      return (
        <div className="flex flex-col 2xl:flex-row items-start p-4">
          <div className="flex-1 max-w-full">
            {Elements ? (
              <Elements
                options={{
                  clientSecret: payment?.clientSecret,
                  appearance: {
                    theme: "stripe" as const,
                  },
                }}
                stripe={stripePromise}
              >
                <CheckoutSteps
                  onSubmit={handleSubmit}
                  setStepIdx={setStep}
                  stepIdx={step}
                  steps={steps}
                  contactEmail={
                    contactEmail || userPermissions?.data?.user?.email
                  }
                  deliveryLocation={deliveryLocation ?? undefined}
                  payment={payment}
                />
              </Elements>
            ) : null}
          </div>
          <div className="flex-1 sticky top-0">
            <ArtList
              items={cart.items.map((item) => ({
                ...item,
                images: item.images?.slice(0, 1),
              }))}
              noArt={<>NO ITEMS IN CART</>}
              getArtConfig={() => ({
                tileClassName:
                  "rounded-2xl border-solid border-2 border-textPrimary shadow-md hover:shadow-lg",
                href: "",
                height: 200,
                hoverHeight: 200,
              })}
            />
          </div>
        </div>
      );
    }

    // Fallback (should never reach here)
    return (
      <div className="p-5">
        An unexpected error occurred. Please refresh the page.
      </div>
    );
  };

  return (
    <Layout loading={checkoutState.status === "loading"} headerStyle="sm">
      <div>{renderContent()}</div>
    </Layout>
  );
}
